import type { JsonObject } from "./types.js";
import { isObject, escapePointer } from "./json.js";

// Vega's parser checks scale names in axes but not in mark encodings or
// expressions: `{ "scale": "nope" }` in an encoding compiles to a lookup that
// quietly yields undefined, so the chart draws empty axes and says nothing.
// This names every reference to a scale its scope does not define.

/** Legend properties whose value is a scale name. */
const LEGEND_CHANNELS = [
  "fill",
  "stroke",
  "size",
  "shape",
  "opacity",
  "strokeDash",
  "strokeWidth",
] as const;

/** Keys whose string value is a Vega expression. */
const EXPRESSION_KEYS = new Set(["signal", "expr", "update", "test", "init"]);

/**
 * Keys a walk doesn't enter: inline rows are data, and a scope's own scales
 * and marks are checked by checkScope.
 */
const SCOPED_KEYS = new Set(["values", "scales", "marks"]);

/** Expression functions whose first argument names a scale. */
const SCALE_FUNCTION =
  /\b(?:scale|invert|copy|domain|range|bandwidth|gradient)\s*\(\s*(["'])([^"']+)\1/g;

class ScaleChecker {
  readonly problems: string[] = [];

  private report(path: string, name: string, scope: ReadonlySet<string>) {
    if (scope.has(name)) return;
    const defined = scope.size > 0 ? [...scope].join(", ") : "none";
    this.problems.push(
      `${path}: unknown scale "${name}" (defined: ${defined})`,
    );
  }

  /** Walks one value within a scope, skipping nested group marks' scopes. */
  private walk(value: unknown, path: string, scope: ReadonlySet<string>) {
    if (Array.isArray(value)) {
      value.forEach((item, i) => this.walk(item, `${path}/${i}`, scope));
      return;
    }
    if (!isObject(value)) return;
    for (const [key, child] of Object.entries(value)) {
      if (SCOPED_KEYS.has(key)) continue;
      this.walkEntry(key, child, `${path}/${escapePointer(key)}`, scope);
    }
  }

  /** Checks one property: a scale name, an expression's scales, or inside it. */
  private walkEntry(
    key: string,
    child: unknown,
    at: string,
    scope: ReadonlySet<string>,
  ) {
    if (typeof child !== "string") return this.walk(child, at, scope);
    if (key === "scale") return this.report(at, child, scope);
    if (!EXPRESSION_KEYS.has(key)) return;
    for (const match of child.matchAll(SCALE_FUNCTION)) {
      this.report(at, match[2] ?? "", scope);
    }
  }

  /** Checks a scope — the spec or a group mark — and the groups inside it. */
  checkScope(node: JsonObject, path: string, parent: ReadonlySet<string>) {
    const scope = new Set(parent);
    if (Array.isArray(node.scales)) {
      for (const def of node.scales) {
        if (isObject(def) && typeof def.name === "string") scope.add(def.name);
      }
    }
    if (Array.isArray(node.legends)) {
      node.legends.forEach((legend: unknown, i) => {
        if (!isObject(legend)) return;
        for (const channel of LEGEND_CHANNELS) {
          const name = legend[channel];
          if (typeof name === "string") {
            this.report(`${path}/legends/${i}/${channel}`, name, scope);
          }
        }
      });
    }
    this.walk(node, path, scope);
    if (!Array.isArray(node.marks)) return;
    node.marks.forEach((mark: unknown, i) => {
      if (!isObject(mark)) return;
      const at = `${path}/marks/${i}`;
      if (mark.type === "group") this.checkScope(mark, at, scope);
      else this.walk(mark, at, scope);
    });
  }
}

/** Every reference to a scale not defined in its scope, with its JSON pointer. */
export function findUnknownScales(spec: JsonObject): string[] {
  const checker = new ScaleChecker();
  checker.checkScope(spec, "", new Set());
  return checker.problems.sort();
}
